import { Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, takeUntil } from 'rxjs/operators';
import { ISelectedContract, LoggedInUserSettings } from 'src/app/core/services/settings/logged-in-user-settings';
import { getFamilyMemberSort, IFamilyMember } from 'src/app/model/contract/IContract';
import { UserClaims } from 'src/app/model/okta/userClaims';
import { AddAMemberModalWindowComponent } from 'src/app/shared/components/family/add-a-member/add-a-member-modal-window.component';
import { ContractGuard } from '../../../../core/guards/contract.guard';
import { DeleteMemberModalComponent } from '../delete-member-modal/delete-member-modal.component';
import { EditAvatarModalComponent } from '../edit-avatar-modal/edit-avatar-modal.component';
import { EditMemberDetailsModalComponent } from '../edit-member-details-modal/edit-member-details-modal.component';
import { AccountService } from 'src/app/core/services/account.service';
import { ADPResourceService } from 'digital-core';

@Component({
  selector: 'pgh-family-management',
  templateUrl: './family-management.component.html',
  styleUrls: ['./family-management.component.scss'],
})
export class FamilyManagementComponent implements OnInit, OnDestroy {
  fMemberList: IFamilyMember[];
  familyMemberList$: Observable<IFamilyMember[]>;
  isPromotionDisabled = false;
  user: UserClaims;
  contract: ISelectedContract;
  isActive: boolean = false;

  isDialogLoaded: boolean = false;
  initialLoad: boolean = false;

  private users: { name: string; initials: string; color: string }[] = [];

  private $destroy = new Subject<boolean>();

  constructor(
    private loggedInUserSettings: LoggedInUserSettings,
    private matDialog: MatDialog,
    private contractGuard: ContractGuard,
    private accountService: AccountService,
    private resourceService: ADPResourceService
  ) {}

  ngOnInit() {
    combineLatest([this.loggedInUserSettings.$selectedContract, this.loggedInUserSettings.$userClaims])
      .pipe(
        filter(([a, b]) => a !== null && b !== null),
        takeUntil(this.$destroy)
      )
      .subscribe(([contract, user]) => {
        this.fMemberList = contract.members.sort(getFamilyMemberSort(contract.primaryUserEmail, user.email));

        //this.users.push({name:this.initials, initials:this.avatarText, color:this.color});
        this.accountService.assignColors(this.fMemberList);
        this.isPromotionDisabled =
          (contract.isCancelled || contract.isExpired) &&
          !this.resourceService.isPortalSettingTrue('Global_CancelledOrExpiredContract_HasFullAccess');
        this.user = user;
        this.contract = contract;
        this.isActive = this.contractGuard.activateGuard(contract, false);

        this.familyMemberList$ = of(this.fMemberList);
      });

    this.initialLoad = true;
  }

  ngOnDestroy() {
    this.$destroy.next(true);
  }

  openAddFamilyMemberModal() {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.disableClose = true;
    dialogConfig.id = 'add-a-member-dialog';
    dialogConfig.height = 'auto';
    dialogConfig.ariaLabel = this.resourceService.getLabel('Account_Manage_Members_AddMember_Heading');
    dialogConfig.data = {};

    this.matDialog.open(AddAMemberModalWindowComponent, dialogConfig);
  }

  openEditMemberDetailsModal(member) {
    this.initialLoad = false;
    this.isDialogLoaded = false;
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      member,
      user: this.user,
    };

    const dialogRef = this.matDialog.open(EditMemberDetailsModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((res) => {
      if (res.data) {
        this.isDialogLoaded = true;
        this.familyMemberList$ = null;

        setTimeout(() => {
          this.familyMemberList$ = of(this.fMemberList);
        }, 100);
      }
    });
  }

  openEditAvatarModal(member) {
    this.initialLoad = false;
    this.isDialogLoaded = false;

    const dialogConfig = new MatDialogConfig();

    dialogConfig.ariaLabel = this.resourceService.getPortalLabel('Global_EditAvatar_ARIA_Label').Value;

    dialogConfig.data = {
      member,
      user: this.user,
      contract: this.contract,
    };

    const dialogRef = this.matDialog.open(EditAvatarModalComponent, dialogConfig);

    dialogRef.afterClosed().subscribe((res) => {
      if (res.data) {
        this.isDialogLoaded = true;
        this.familyMemberList$ = null;
        setTimeout(() => {
          this.familyMemberList$ = of(this.fMemberList);
        }, 100);
      }
    });
  }

  openDeleteMemberModal(member) {
    const dialogConfig = new MatDialogConfig();

    dialogConfig.data = {
      member,
      contract: this.contract,
    };

    this.matDialog
      .open(DeleteMemberModalComponent, dialogConfig)
      .afterClosed()
      .pipe(filter((result) => result))
      .subscribe(() => {
        this.contract.members.find((m) => m.emailAddress === member.emailAddress).profileStatus = 'I';
        this.loggedInUserSettings.setSelectedContract(this.contract);
      });
  }

  getViewLabel(item: string) {
    const label = this.resourceService.getViewLabel(item) ?? '';
    return this.resourceService.transformContentData(label);
  }
}
